/**
 * VideoAttachClient — TypeScript port of app/static/js/video-attach.js
 * (loaded by the Jinja template via <script src>). Page-agnostic video
 * upload pipeline for attaching a video to an existing game via the
 * /video/* Flask endpoints:
 *   - Single-file presigned-URL upload (files < 5GB).
 *   - Multipart upload with abort-on-error (files >= 5GB).
 * Does NOT handle UI — callers pass onProgress and render their own display.
 */

const SINGLE_UPLOAD_MAX_SIZE = 5 * 1024 * 1024 * 1024 // 5GB

export interface UploadProgress {
  percent: number
  status: string
  partNumber?: number
  totalParts?: number
}

export interface UploadResult {
  videoUrl: string
  videoPath: string
}

interface Envelope {
  success?: boolean
  error?: string
  [key: string]: unknown
}

async function postJson(url: string, body: unknown): Promise<Envelope> {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return (await response.json()) as Envelope
}

function putToR2(
  url: string,
  body: Blob,
  contentType: string | null,
  onLoaded: (loaded: number) => void,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onLoaded(e.loaded)
    })
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr)
      else reject(new Error(`Upload failed with status ${xhr.status}`))
    }
    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.onabort = () => reject(new Error('Upload was aborted'))
    xhr.open('PUT', url)
    if (contentType) xhr.setRequestHeader('Content-Type', contentType)
    xhr.send(body)
  })
}

export class VideoAttachClient {
  private onProgress: (p: UploadProgress) => void

  constructor({ onProgress }: { onProgress?: (p: UploadProgress) => void } = {}) {
    this.onProgress = onProgress || (() => {})
  }

  /** Upload a video file and associate it with the given game. */
  async upload(file: File, gameCode: string): Promise<UploadResult> {
    if (!file) throw new Error('No file provided')
    if (!gameCode) throw new Error('No game code provided')

    const contentType = file.type || 'video/mp4'

    if (file.size >= SINGLE_UPLOAD_MAX_SIZE) {
      return await this.uploadMultipart(file, gameCode, contentType)
    }
    return await this.uploadSingle(file, gameCode, contentType)
  }

  private async uploadSingle(
    file: File,
    gameCode: string,
    contentType: string,
  ): Promise<UploadResult> {
    const presignData = await postJson(`/api/v1/video/presigned-url/${gameCode}`, {
      filename: file.name,
      file_size: file.size,
      content_type: contentType,
    })
    if (!presignData.success) {
      throw new Error(presignData.error || 'Failed to get upload URL')
    }

    await putToR2(presignData.upload_url as string, file, contentType, (loaded) => {
      this.onProgress({
        percent: (loaded / file.size) * 100,
        status: 'Uploading to cloud…',
      })
    })

    const completeData = await postJson(`/api/v1/video/complete/${gameCode}`, {
      r2_key: presignData.r2_key,
    })
    if (!completeData.success) {
      throw new Error(completeData.error || 'Failed to complete upload')
    }

    return {
      videoUrl: completeData.video_url as string,
      videoPath: completeData.video_path as string,
    }
  }

  private async uploadMultipart(
    file: File,
    gameCode: string,
    contentType: string,
  ): Promise<UploadResult> {
    let uploadId: string | null = null
    let r2Key: string | null = null

    try {
      const initData = await postJson(`/api/v1/video/multipart/init/${gameCode}`, {
        filename: file.name,
        file_size: file.size,
        content_type: contentType,
      })
      if (!initData.success) {
        throw new Error(initData.error || 'Failed to initialize upload')
      }

      uploadId = initData.upload_id as string
      r2Key = initData.r2_key as string
      const partSize = initData.part_size as number
      const totalParts = initData.total_parts as number

      const parts: { part_number: number; etag: string | null }[] = []
      let bytesUploaded = 0

      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * partSize
        const end = Math.min(start + partSize, file.size)
        const chunk = file.slice(start, end)

        const partData = await postJson(`/api/v1/video/multipart/presign-part/${gameCode}`, {
          upload_id: uploadId,
          r2_key: r2Key,
          part_number: partNumber,
        })
        if (!partData.success) {
          throw new Error(partData.error || `Failed to get URL for part ${partNumber}`)
        }

        const xhr = await putToR2(partData.upload_url as string, chunk, null, (loaded) => {
          this.onProgress({
            percent: ((bytesUploaded + loaded) / file.size) * 100,
            status: `Uploading part ${partNumber}/${totalParts}…`,
            partNumber,
            totalParts,
          })
        })
        let etag = xhr.getResponseHeader('ETag')
        if (etag) etag = etag.replace(/"/g, '')

        parts.push({ part_number: partNumber, etag })
        bytesUploaded += chunk.size
      }

      const completeData = await postJson(`/api/v1/video/multipart/complete/${gameCode}`, {
        upload_id: uploadId,
        r2_key: r2Key,
        parts,
      })
      if (!completeData.success) {
        throw new Error(completeData.error || 'Failed to complete multipart upload')
      }

      return {
        videoUrl: completeData.video_url as string,
        videoPath: completeData.video_path as string,
      }
    } catch (error) {
      if (uploadId && r2Key) {
        try {
          await postJson(`/api/v1/video/multipart/abort/${gameCode}`, {
            upload_id: uploadId,
            r2_key: r2Key,
          })
        } catch (abortError) {
          console.error('Failed to abort multipart upload:', abortError)
        }
      }
      throw error
    }
  }
}
